<!--
  ReservationManagement.vue
  예약 관리 메인 페이지
  
  주요 기능:
  - 예약 목록 조회 및 관리
  - 예약 검색 및 필터링
  - 예약 상태 변경
  - 예약 생성/수정/삭제
  - 통계 정보 표시
-->
<template>
  <div style="padding: 1vw">
    <div class="container-wrapper">
      <div class="page-title-container">
        <div class="page-title">
          <h3 class="fw-bold" style="color: #000; font-size: 1.8rem">
            예약 관리
          </h3>
          <span class="pageDesc">
            병원 예약을 조회하고 관리할 수 있습니다.
          </span>
        </div>
        <div style="display: flex; align-items: center;">
          <!-- 통계 정보 표시 -->
          <div class="stats-container">
            <div 
              class="stat-item" 
              :class="{ active: searchFilter.reservationStatus === '' }"
              @click="filterByStatus('')"
            >
              <span class="stat-label">전체 예약</span>
              <span class="stat-value">{{ formatCommaNumber(stats.totalReservations) }}</span>
            </div>
            <div 
              class="stat-item" 
              :class="{ active: searchFilter.reservationStatus === 'waiting' }"
              @click="filterByStatus('waiting')"
            >
              <span class="stat-label">예약 대기</span>
              <span class="stat-value waiting">{{ formatCommaNumber(stats.waitingReservations) }}</span>
            </div>
            <div 
              class="stat-item" 
              :class="{ active: searchFilter.reservationStatus === 'confirmed' }"
              @click="filterByStatus('confirmed')"
            >
              <span class="stat-label">예약 확정</span>
              <span class="stat-value confirmed">{{ formatCommaNumber(stats.confirmedReservations) }}</span>
            </div>
            <div 
              class="stat-item" 
              :class="{ active: searchFilter.reservationStatus === 'completed' }"
              @click="filterByStatus('completed')"
            >
              <span class="stat-label">진료 완료</span>
              <span class="stat-value completed">{{ formatCommaNumber(stats.completedReservations) }}</span>
            </div>
            <div 
              class="stat-item" 
              :class="{ active: searchFilter.reservationStatus === 'cancelled' }"
              @click="filterByStatus('cancelled')"
            >
              <span class="stat-label">예약 취소</span>
              <span class="stat-value cancelled">{{ formatCommaNumber(stats.cancelledReservations) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 검색 필터 영역 -->
    <ReservationSearchFilter
      :search-filter="searchFilter"
      :manufacturer-list="manufacturerList"
      :category-list="categoryList"
      :dept-list="deptList"
      @search="handleSearch"
      @reset="handleReset"
    />

    <div class="container-wrapper">
      <div class="col-xs-12 table-info-filter">
        <div class="total-div">
          <p>
            전체 <span>{{ formatCommaNumber(listSize) }}</span>건
          </p>
        </div>
        <div class="search-container">
          <!-- <a-button
            @click="downloadExcel"
            type="primary"
            :loading="excelLoading"
          >
            <i class="fa-solid fa-download"/>
            엑셀 다운로드
          </a-button> -->
          <a-button
            @click="openSuperAdminModal"
            type="primary"
          >
            슈퍼 담당자 관리
          </a-button>
          <select class="search-select" v-model="rowsPerPage" @change="handleSearch">
            <option :value="10">10개씩 보기</option>
            <option :value="20">20개씩 보기</option>
            <option :value="50">50개씩 보기</option>
          </select>
        </div>
      </div>
    </div>

    <!-- 예약 목록 테이블 -->
    <div class="container-wrapper">
      <a-table 
        v-if="reservationList.length > 0"
        class="ant-table-striped"
        size="small"
        :columns="columns"
        :data-source="reservationList"
        :pagination="false"
        :loading="loading"
        row-key="_id"
        :resizable="false"
        table-layout="fixed"
      >
        <template #emptyText>
          <div class="noResult">검색 결과가 없습니다.</div>
        </template>
      </a-table>
      
      <div v-else-if="!loading" class="empty-state">
        <i class="fa fa-calendar-o" style="font-size: 48px; color: #ccc; margin-bottom: 16px"></i>
        <p>아직 등록된 예약이 없습니다.</p>
      </div>
    </div>

    <!-- 페이지네이션 -->
    <pagination 
      :page-info="paginationData"
      page-name="ReservationList"
      @change-page="paginationHandler"
    />



    <!-- 예약 수정 모달 -->
    <ReservationModal
      :open="modalVisible"
      :loading="modalLoading"
      :is-edit="isEdit"
      :reservation-data="currentReservationData"
      @ok="handleModalOk"
      @cancel="handleModalCancel"
    />
    
    <!-- 슈퍼 관리자 관리 모달 -->
    <SuperAdminModal
      :visible="superAdminModalVisible"
      @cancel="handleSuperAdminModalCancel"
    />
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, nextTick, getCurrentInstance, shallowRef, markRaw, onBeforeUnmount } from 'vue';
import { message } from 'ant-design-vue';
import { twoBtnDialog, formatCommaNumber, convertDate, convertToLocalTime, phoneNumberFormatter } from '@/utils/utils';
import { MedicalDept, convertMedicalDept } from '@/enums/common.enums';
import Pagination from '@/views/components/Pagination.vue';
import ReservationSearchFilter from './components/ReservationSearchFilter.vue';
import ReservationModal from './components/ReservationModal.vue';
import SuperAdminModal from './components/SuperAdminModal.vue';

export default {
  name: 'ReservationManagement',
  components: {
    Pagination,
    ReservationSearchFilter,
    ReservationModal,
    SuperAdminModal
  },
  setup() {
    // API 인스턴스 초기화
    const instance = getCurrentInstance();
    let apiInstance = null;
    
    if (instance && instance.proxy) {
      apiInstance = instance.proxy.$apiInstance;
    } else {
      console.error('Current instance not available, API initialization failed');
    }

    // 반응형 상태 정의
    const loading = ref(false);
    const excelLoading = ref(false);
    const modalVisible = ref(false);
    const modalLoading = ref(false);
    const isEdit = ref(false);
    const superAdminModalVisible = ref(false);

    // 데이터 상태
    const reservationList = shallowRef([]);
    const manufacturerList = shallowRef([]);
    const categoryList = shallowRef([]);
    const deptList = shallowRef([]);
    const currentReservationData = ref({});
    
    // 통계 정보
    const stats = reactive({
      totalReservations: 0,
      waitingReservations: 0,
      confirmedReservations: 0,
      completedReservations: 0,
      cancelledReservations: 0
    });

    // 검색 필터
    const searchFilter = reactive({
      filter: 'customerName',
      keyword: '',
      reservationStatus: '',
      selectedManufacturer: '',
      selectedCategory: '',
      selectedDept: '',
      startAt: null,
      endAt: null,
      selectedDateFilter: 'all',
      dateFilters: [3, 12, 36, 'etc']
    });
    
    // 페이지네이션
    const listSize = ref(0);
    const rowsPerPage = ref(10);
    const paginationData = reactive({
      currentPage: 1,
      totalPages: 0
    });

    // 상태 매핑 (백엔드 상태값에 맞춰 수정)
    const statusMap = markRaw({
      'PENDING': { text: '예약 대기', class: 'waiting' },
      'APPROVED': { text: '예약 확정', class: 'confirmed' },
      'COMPLETED': { text: '진료 완료', class: 'completed' },
      'CANCELLED': { text: '예약 취소', class: 'cancelled' }
    });

    // 렌더링 함수들
    const renderPhoneNumber = ({ record }) => {
      return phoneNumberFormatter(record.phoneNumber);
    };

    const renderBirthDate = ({ record }) => {
      return convertDate(record.birthDate);
    };

    const renderReservationDate = ({ record }) => {
      return convertDate(record.reservationDate);
    };

    const renderPreferredTime = ({ record }) => {
      return record.preferredTime || '-';
    };

    const renderCreateTime = ({ record }) => {
      return convertToLocalTime(record.createAt);
    };

    // 진료과 렌더링
    const renderMedicalDept = ({ record }) => {
      return convertMedicalDept(record.medicalDept) || record.medicalDept || '-';
    };

    // 상태 렌더링
    const renderStatus = ({ record }) => {
      const statusInfo = statusMap[record.reservationStatus] || statusMap['PENDING'];
      
      return (
        <div class="status-chip-wrap">
          <div class={['status-chip', statusInfo.class]}>
            {statusInfo.text}
          </div>
        </div>
      );
    };

    // 작업 버튼 렌더링
    const renderActions = ({ record }) => {
      return (
        <div style="display: flex; gap: 8px; justify-content: center;">
          <a-button size="small" onClick={() => handleEdit(record)}>
            상세
          </a-button>
          {/* <a-button 
            size="small" 
            danger 
            onClick={() => handleDelete(record._id)}
          >
            삭제
          </a-button> */}
        </div>
      );
    };
    
    // 테이블 컬럼 정의
    const columns = markRaw([
      {
        title: '번호',
        dataIndex: 'index',
        key: 'index',
        width: 60,
        align: 'center'
      },
      {
        title: '예약 등록 일자',
        dataIndex: 'createAt',
        key: 'createAt',
        width: 150,
        align: 'center',
        customRender: renderCreateTime
      },
      {
        title: '예약 희망 시간',
        dataIndex: 'preferredTime',
        key: 'preferredTime',
        width: 150,
        align: 'center',
        customRender: renderPreferredTime
      },
      {
        title: '이름',
        dataIndex: 'customerName',
        key: 'customerName',
        width: 120,
        align: 'center'
      },
      {
        title: '생년월일',
        dataIndex: 'birthDate',
        key: 'birthDate',
        width: 120,
        align: 'center',
        customRender: renderBirthDate
      },
      {
        title: '모바일',
        dataIndex: 'phoneNumber',
        key: 'phoneNumber',
        width: 150,
        align: 'center',
        customRender: renderPhoneNumber
      },
      {
        title: '병원명',
        dataIndex: 'companyName',
        key: 'companyName',
        width: 150,
        align: 'center'
      },
      {
        title: '시술명',
        dataIndex: 'categoryName',
        key: 'categoryName',
        width: 120,
        align: 'center'
      },
      {
        title: '진료과',
        dataIndex: 'medicalDept',
        key: 'medicalDept',
        width: 100,
        align: 'center',
        customRender: renderMedicalDept
      },
      {
        title: '예약 상태',
        dataIndex: 'reservationStatus',
        key: 'reservationStatus',
        width: 120,
        align: 'center',
        customRender: renderStatus
      },
      {
        title: '작업',
        key: 'action',
        width: 150,
        align: 'center',
        customRender: renderActions
      }
    ]);

    // 방抖搜索优化
    let searchTimeout = null;
    const debouncedSearch = (callback, delay = 300) => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
      searchTimeout = setTimeout(callback, delay);
    };

    // 공통 에러 처리 함수
    const handleError = (error, defaultMessage) => {
      console.error('Error occurred:', error);
      
      let errorMessage = defaultMessage;
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      message.error(errorMessage);
      return errorMessage;
    };

    // 예약 목록 로드
    const loadReservations = async (shouldLoadStats = true) => {
      if (!apiInstance) {
        message.error('API가 초기화되지 않았습니다.');
        return;
      }
      
      if (shouldLoadStats) {
        loadStats();
      }
      
      loading.value = true;
      
      try {
        const params = {
          page: paginationData.currentPage,
          limit: rowsPerPage.value
        };
        
        // 검색 조건 추가 (백엔드 DTO 파라미터명에 맞춰 수정)
        if (searchFilter.keyword.trim()) {
          if (searchFilter.filter === 'customerName') {
            params.patientName = searchFilter.keyword.trim();
          } else if (searchFilter.filter === 'phoneNumber') {
            params.phoneNumber = searchFilter.keyword.trim();
          } else if (searchFilter.filter === 'companyName') {
            params.hospitalName = searchFilter.keyword.trim();
          } else if (searchFilter.filter === 'categoryName') {
            params.itemName = searchFilter.keyword.trim();
          }
        }

        if (searchFilter.reservationStatus) params.status = searchFilter.reservationStatus;
        if (searchFilter.selectedManufacturer) params.manufacturerId = searchFilter.selectedManufacturer;
        if (searchFilter.selectedDept) params.medicalDept = searchFilter.selectedDept;
        
        if (searchFilter.startAt && searchFilter.endAt) {
          params.startDate = unixTimeToYYYYMMDD(searchFilter.startAt);
          params.endDate = unixTimeToYYYYMMDD(searchFilter.endAt);
        }
        
        const response = await apiInstance.get('/operation-hospital-reservations', { params });
        
        if (response.data) {
          const data = response.data.docs || [];
          listSize.value = response.data.totalDocs || 0;
          paginationData.totalPages = response.data.totalPages || 0;
          
          // 백엔드 데이터 필드명을 프론트엔드 필드명으로 매핑
          const processedData = data.map((item, index) => ({
            _id: item._id,
            createAt: item.createAt,
            preferredTime: item.reservationDateTime,
            customerName: item.patientName,
            birthDate: item.patientBirthdate,
            phoneNumber: item.patientPhoneNumber,
            contactNumber: item.contactNumber,
            companyName: item.hospitalName,
            categoryName: item.itemName,
            reservationStatus: item.status,
            medicalDept: item.medicalDept,
            index: listSize.value - index - (paginationData.currentPage - 1) * rowsPerPage.value
          }));

          reservationList.value = processedData;
        }
      } catch (error) {
        handleError(error, '예약 목록을 불러오는 중 오류가 발생했습니다.');
        reservationList.value = [];
        listSize.value = 0;
      } finally {
        loading.value = false;
      }
    };

    // 날짜 변환 유틸리티 함수
    const unixTimeToYYYYMMDD = (unixTime) => {
      const date = new Date(unixTime);
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      return `${year}${month}${day}`;
    };

    // 통계 정보 로드
    const loadStats = async () => {
      if (!apiInstance) return;
      
      try {
        const response = await apiInstance.get('/operation-hospital-reservations/status-counts');
        
        if (response.data) {
          stats.totalReservations = response.data.ALL || 0;
          stats.waitingReservations = response.data.PENDING || 0;
          stats.confirmedReservations = response.data.APPROVED || 0;
          stats.completedReservations = response.data.COMPLETED || 0;
          stats.cancelledReservations = response.data.CANCELLED || 0;
        }
      } catch (error) {
        console.error('통계 정보 로드 실패:', error);
      }
    };

    // 기본 데이터 로드
    const loadBasicData = async () => {
      if (!apiInstance) return;
      
      try {
        const [deptResponse, manufacturerResponse] = await Promise.all([
          apiInstance.get('/items/medical/depts'),
          apiInstance.post('/companies/list/v2', {
            page: 1,
            limit: 1000,
            type: "MANUFACTURER"
          })
        ]);
        
        if (deptResponse.status === 200 || deptResponse.status === 201) {
          deptList.value = deptResponse.data.filter(item => item.code !== MedicalDept.COMMON) || [];
        }
        
        if (manufacturerResponse.status === 200 || manufacturerResponse.status === 201) {
          manufacturerList.value = manufacturerResponse.data.docs || [];
        }
        
      } catch (error) {
        console.error('기본 데이터 로드 실패:', error);
      }
    };

    // 검색 처리
    const handleSearch = () => {
      debouncedSearch(() => {
        paginationData.currentPage = 1;
        loadReservations();
      });
    };

    // 검색 초기화
    const handleReset = () => {
      Object.assign(searchFilter, {
        filter: 'customerName',
        keyword: '',
        reservationStatus: '',
        selectedManufacturer: '',
        selectedCategory: '',
        selectedDept: '',
        startAt: null,
        endAt: null,
        selectedDateFilter: 'all'
      });
      paginationData.currentPage = 1;
      loadReservations();
    };

    // 페이지네이션 처리
    const paginationHandler = (pageInfo) => {
      paginationData.currentPage = pageInfo.page;
      loadReservations(false);
    };

    // 예약 추가
    const handleAdd = () => {
      isEdit.value = false;
      currentReservationData.value = {};
      modalVisible.value = true;
    };

    // 예약 수정
    const handleEdit = (record) => {
      isEdit.value = true;
      currentReservationData.value = { ...record };
      modalVisible.value = true;
    };

    // 예약 삭제
    const handleDelete = async (id) => {
      const isConfirmed = await twoBtnDialog(
        '예약 삭제',
        '정말로 이 예약을 삭제하시겠습니까?',
        '삭제'
      );
      
      if (!isConfirmed) return;
      
      try {
        await apiInstance.delete(`/operation-hospital-reservations/${id}`);
        message.success('예약이 성공적으로 삭제되었습니다.');
        
        reservationList.value = reservationList.value.filter(item => item._id !== id);
        listSize.value = Math.max(0, listSize.value - 1);
        
        if (reservationList.value.length === 0 && paginationData.currentPage > 1) {
          paginationData.currentPage--;
          loadReservations();
        }
      } catch (error) {
        handleError(error, '예약 삭제 중 오류가 발생했습니다.');
        loadReservations();
      }
    };

    // 모달 확인 처리
    const handleModalOk = async (formData) => {
      modalLoading.value = true;
      
      try {
        if (isEdit.value) {
          await apiInstance.put(`/operation-hospital-reservations/${currentReservationData.value._id}`, formData);
          message.success('예약이 성공적으로 수정되었습니다.');
        } else {
          await apiInstance.post('/operation-hospital-reservations', formData);
          message.success('예약이 성공적으로 추가되었습니다.');
        }
        
        modalVisible.value = false;
        loadReservations();
      } catch (error) {
        handleError(error, isEdit.value ? '예약 수정 중 오류가 발생했습니다.' : '예약 추가 중 오류가 발생했습니다.');
      } finally {
        modalLoading.value = false;
      }
    };

    // 모달 취소 처리
    const handleModalCancel = () => {
      modalVisible.value = false;
      currentReservationData.value = {};
    };

    // 슈퍼 관리자 모달 열기
    const openSuperAdminModal = () => {
      superAdminModalVisible.value = true;
    };

    // 슈퍼 관리자 모달 닫기
    const handleSuperAdminModalCancel = () => {
      superAdminModalVisible.value = false;
    };

    // 상태별 필터링 (백엔드 상태값에 맞춰 매핑)
    const filterByStatus = (status) => {
      // 프론트엔드 상태를 백엔드 상태로 매핑
      const statusMap = {
        '': '',
        'waiting': 'PENDING',
        'confirmed': 'APPROVED', 
        'completed': 'COMPLETED',
        'cancelled': 'CANCELLED'
      };
      
      searchFilter.reservationStatus = statusMap[status] || status;
      paginationData.currentPage = 1;
      loadReservations();
    };

    // 엑셀 다운로드
    const downloadExcel = async () => {
      if (!apiInstance) {
        message.error('API가 초기화되지 않았습니다.');
        return;
      }
      
      excelLoading.value = true;
      
      try {
        const params = {};
        
        if (searchFilter.keyword.trim()) {
          if (searchFilter.filter === 'customerName') {
            params.patientName = searchFilter.keyword.trim();
          } else if (searchFilter.filter === 'phoneNumber') {
            params.phoneNumber = searchFilter.keyword.trim();
          } else if (searchFilter.filter === 'companyName') {
            params.hospitalName = searchFilter.keyword.trim();
          } else if (searchFilter.filter === 'categoryName') {
            params.itemName = searchFilter.keyword.trim();
          }
        }
        
        if (searchFilter.reservationStatus) params.status = searchFilter.reservationStatus;
        if (searchFilter.selectedManufacturer) params.manufacturerId = searchFilter.selectedManufacturer;
        if (searchFilter.selectedDept) params.medicalDept = searchFilter.selectedDept;
        
        if (searchFilter.startAt && searchFilter.endAt) {
          params.startDate = unixTimeToYYYYMMDD(searchFilter.startAt);
          params.endDate = unixTimeToYYYYMMDD(searchFilter.endAt);
        }
        
        const response = await apiInstance.get('/operation-hospital-reservations/download/excel', { 
          params,
          responseType: 'blob'
        });
        
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        const now = new Date();
        const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
        link.download = `예약관리_${dateStr}.xlsx`;
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        message.success('엑셀 파일이 다운로드되었습니다.');
      } catch (error) {
        handleError(error, '엑셀 다운로드 중 오류가 발생했습니다.');
      } finally {
        excelLoading.value = false;
      }
    };

    // 컴포넌트 언마운트 시 정리
    const cleanup = () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };

    // 컴포넌트 마운트 시 데이터 로드
    onMounted(async () => {
      await Promise.all([
        loadBasicData(),
        loadReservations()
      ]);
    });

    // 컴포넌트 언마운트 시 정리
    onBeforeUnmount(() => {
      cleanup();
    });

    return {
      // 상태
      loading,
      excelLoading,
      modalVisible,
      modalLoading,
      isEdit,
      superAdminModalVisible,
      
      // 데이터
      reservationList,
      manufacturerList,
      categoryList,
      deptList,
      currentReservationData,
      stats,
      
      // 검색 및 페이지네이션
      searchFilter,
      listSize,
      rowsPerPage,
      paginationData,
      
      // 테이블
      columns,
      
      // 이벤트 핸들러
      handleSearch,
      handleReset,
      paginationHandler,
      handleAdd,
      handleEdit,
      handleDelete,
      handleModalOk,
      handleModalCancel,
      downloadExcel,
      filterByStatus,
      openSuperAdminModal,
      handleSuperAdminModalCancel,
      
      // 유틸 함수
      formatCommaNumber,
      convertToLocalTime
    };
  }
};
</script>

<style scoped lang="scss">
@import '@/assets/scss/certificates.scss';

.container-wrapper {
  margin-bottom: 20px;
}

.page-title-container {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.page-title {
  h3 {
    margin-bottom: 8px;
  }
  
  .pageDesc {
    color: #6c757d;
    font-size: 14px;
  }
}

.stats-container {
  display: flex;
  gap: 20px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 16px;
  background: #f8f9fa;
  border-radius: 8px;
  min-width: 80px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  
  &:hover {
    background: #e9ecef;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  &.active {
    background: #e6f7ff;
    border-color: #1890ff;
    box-shadow: 0 2px 8px rgba(24, 144, 255, 0.2);
    
    .stat-label {
      color: #1890ff;
      font-weight: 600;
    }
  }
  
  .stat-label {
    font-size: 12px;
    color: #666;
    margin-bottom: 4px;
    transition: all 0.3s ease;
  }
  
  .stat-value {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    
    &.waiting {
      color: #fa8c16;
    }
    
    &.confirmed {
      color: #1890ff;
    }
    
    &.completed {
      color: #52c41a;
    }
    
    &.cancelled {
      color: #f5222d;
    }
  }
}

:deep(.status-chip-wrap) {
  display: flex;
  justify-content: center;
}

:deep(.status-chip) {
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  min-width: 70px;
  border: 1px solid transparent;
  
  &.waiting {
    background-color: #fff7e6 !important;
    color: #d46b08 !important;
    border-color: #ffd591 !important;
  }
  
  &.confirmed {
    background-color: #e6f7ff !important;
    color: #1890ff !important;
    border-color: #91d5ff !important;
  }
  
  &.completed {
    background-color: #f6ffed !important;
    color: #389e0d !important;
    border-color: #b7eb8f !important;
  }
  
  &.cancelled {
    background-color: #fff1f0 !important;
    color: #cf1322 !important;
    border-color: #ffa39e !important;
  }
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #999;
  
  p {
    margin: 16px 0;
    font-size: 16px;
  }
}

:deep(.yellow-button) {
  background-color: #FFCC32 !important;
  border-color: #FFCC32 !important;
  color: #000 !important;
  
  &:hover {
    background-color: #E6B82E !important;
    border-color: #E6B82E !important;
  }
  
  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
  }
}

// 반응형 스타일
@media screen and (max-width: 768px) {
  .page-title-container {
    flex-direction: column;
    gap: 16px;
  }
  
  .stats-container {
    width: 100%;
    justify-content: space-around;
  }
  
  .search-container {
    flex-direction: column;
    gap: 8px;
    
    .ant-btn {
      width: 100%;
    }
  }
}
</style> 